import { hasSelectedReport, selectReports } from '@ndt/reports/shared/effective-reports/effective-reports.selector';
import { Reports } from '@ndt/reports/shared/reports.enum';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { extensionQueueOptionSelectors } from 'src/app/common-components/entity-select/extension/state/extension-option-reducers/extension-queue-option.reducer';
import {
   accountActiveCallsRoute,
   accountCallHistoryRoute,
   accountDevicesRoute,
   accountLogsRoute,
   accountPhoneNumbersRoute,
   accountQueuesRoute,
   accountSettingsRoute,
   accountsRoute,
   activeCallsRoute,
   allowedIpsRoute,
   bannedIpsRoute,
   blockedPhoneNumbersRoute,
   callCostsRoute,
   departmentsRoute,
   devicesRoute,
   extensionGroupRoute,
   extensionsRoute,
   locationsRoute,
   mailboxesRoute,
   phoneNumbersRoute,
   presenceContactsRoute,
   promptsRoute,
   rolesRoute,
   schedulesRoute,
   systemLogsRoute,
   systemRolesRoute,
   systemUsersRoute,
   usersRoute,
} from 'src/app/app-routing.module';
import { Privileges } from 'src/app/privileges/privileges';
import { getIsOnSystemPage, getSelectedAccount } from 'src/app/store/select-account/select-account.selector';
import {
   getCurrentUser,
   hasPrivilege,
   hasSelectedPrivilege,
   selectPrivileges,
} from '../../../authentication/state/authentication.selector';
import { MenuItem } from '../site-menu/menu-item';
import { truncateAccountName } from '../views/site-layout-view.component';
import { SiteLayoutState, siteLayoutReducerKey } from './site-layout.reducer';
export const selectSiteLayoutFeature = createFeatureSelector<SiteLayoutState>(siteLayoutReducerKey);

export const selectMenuItems = createSelector(
   selectSiteLayoutFeature,
   getCurrentUser,
   getIsOnSystemPage,
   getSelectedAccount,
   extensionQueueOptionSelectors.selectState,
   selectPrivileges,
   selectReports,
   hasPrivilege(Privileges.ManageAccountSettings_View),
   hasPrivilege(Privileges.ManageCallDetails_View),
   hasPrivilege(Privileges.ManagePhoneNumberSettings_View),
   hasPrivilege(Privileges.ManageDevices_View),
   hasPrivilege(Privileges.ManageSystemUsers_View),
   hasPrivilege(Privileges.ManageSystemRoles_View),
   hasPrivilege(Privileges.ManageSystemLogs_View),
   hasPrivilege(Privileges.ManageProviderCallRates_View),
   hasPrivilege(Privileges.ManageBannedAllowedIps_Enabled),
   (
      state,
      currentUser,
      isOnSystemPage,
      account,
      queueExtensionOptions,
      privileges,
      effectiveReports,
      canViewAccounts,
      canViewActiveCalls,
      canViewPhoneNumbers,
      canViewDevices,
      canViewSystemUsers,
      canViewSystemRoles,
      canViewSystemLogs,
      canViewCallCosts,
      canManageBannedIps
   ) => {
      const menuItems: MenuItem[] = [];

      menuItems.push({
         icon: 'dashboard',
         name: 'Dashboard',
         routerLink: 'dashboard',
         showInCollapsedMenu: true,
         tooltip: 'Dashboard',
         isHidden: !currentUser?.isTrueSystemUser,
      });

      if (canViewAccounts) {
         menuItems.push({
            icon: 'account_box',
            name: 'Accounts',
            routerLink: accountsRoute,
            showInCollapsedMenu: true,
            tooltip: 'Accounts',
            isHidden: !canViewAccounts || !currentUser?.isTrueSystemUser,
         });
      }

      const canManageAccounts = hasSelectedPrivilege(
         Privileges.ManageAccountSettings_View,
         account.accountId,
         privileges
      );
      const accountLength = account?.name?.length ?? 0;
      const truncatedAccountName = accountLength > 20 ? truncateAccountName(account.name, 30) : account.name;
      menuItems.push({
         icon: 'settings',
         isIndented: currentUser?.isTrueSystemUser,
         name: `${truncatedAccountName} Settings`,
         routerLink: `${accountSettingsRoute}/${account.accountId}`,
         showInCollapsedMenu: true,
         tooltip: 'Account Settings',
         isHidden: isOnSystemPage || !canManageAccounts,
      });

      const canManageUsers = hasSelectedPrivilege(
         Privileges.ManageUsersAndPrivileges_View,
         account.accountId,
         privileges
      );
      const canManageRoles = hasSelectedPrivilege(Privileges.ManageRoles_View, account.accountId, privileges);
      const userItems: MenuItem[] = [];
      userItems.push({
         icon: 'person',
         isIndented: false,
         name: 'Users',
         routerLink: usersRoute,
         showInCollapsedMenu: true,
         tooltip: 'Users',
         isHidden: isOnSystemPage || !canManageUsers,
      });

      userItems.push({
         icon: 'group',
         isIndented: false,
         name: 'Roles',
         routerLink: rolesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Roles',
         isHidden: isOnSystemPage || !canManageRoles,
      });

      const userMenu: MenuItem = {
         children: userItems,
         icon: 'group',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'User Management',
         showInCollapsedMenu: true,
         tooltip: 'User Management',
         isHidden: isOnSystemPage || (!canManageUsers && !canManageRoles),
      };

      menuItems.push(userMenu);

      const canViewActiveCallPage = hasSelectedPrivilege(
         Privileges.ManageActiveCalls_View,
         account.accountId,
         privileges
      );
      menuItems.push({
         icon: 'phone_in_talk',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Active Calls',
         routerLink: accountActiveCallsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Active Calls',
         isHidden: isOnSystemPage || !canViewActiveCallPage,
      });

      const canViewCallHisory = hasSelectedPrivilege(Privileges.ManageCallDetails_View, account.accountId, privileges);
      menuItems.push({
         icon: 'history',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Call History',
         routerLink: accountCallHistoryRoute,
         showInCollapsedMenu: true,
         tooltip: 'Call History',
         isHidden: isOnSystemPage || !canViewCallHisory,
      });

      const canManageExtensions = hasSelectedPrivilege(
         Privileges.ManageExtensions_Enabled,
         account.accountId,
         privileges
      );
      const canViewQueueStatus = hasSelectedPrivilege(Privileges.ManageQueueStatus_View, account.accountId, privileges);
      const hasCallQueueExtensions =
         queueExtensionOptions.filters?.some((filter) => {
            return filter.split(' AND ').includes(`accountId = ${account.accountId}`);
         }) && queueExtensionOptions.ids.length > 0;
      menuItems.push({
         icon: 'support_agent',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Queue Status',
         routerLink: accountQueuesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Queue Status',
         isHidden: isOnSystemPage || !canViewQueueStatus || !hasCallQueueExtensions,
      });

      menuItems.push({
         icon: 'phone',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Extensions',
         routerLink: extensionsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Extensions',
         isHidden: isOnSystemPage || !canManageExtensions,
      });

      menuItems.push({
         icon: 'group',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Extension Groups',
         routerLink: extensionGroupRoute,
         showInCollapsedMenu: true,
         tooltip: 'Extensions Groups',
         isHidden: isOnSystemPage || !canManageExtensions,
      });

      if (hasSelectedPrivilege(Privileges.ManageExtensions_Enabled, account.accountId, privileges)) {
         menuItems.push({
            icon: 'contact_page',
            isIndented: currentUser?.isTrueSystemUser,
            name: 'Presence',
            routerLink: presenceContactsRoute,
            showInCollapsedMenu: true,
            tooltip: 'Presence',
            isHidden: isOnSystemPage || !canManageExtensions,
         });
      }

      menuItems.push({
         icon: 'mail',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Mailboxes',
         routerLink: mailboxesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Mailboxes',
         isHidden: isOnSystemPage || !canManageExtensions,
      });

      const canManagePhoneNumbers = hasSelectedPrivilege(
         Privileges.ManagePhoneNumberSettings_View,
         account.accountId,
         privileges
      );
      menuItems.push({
         icon: 'dialpad',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Phone Numbers',
         routerLink: accountPhoneNumbersRoute,
         showInCollapsedMenu: true,
         tooltip: 'Phone Numbers',
         isHidden: isOnSystemPage || !canManagePhoneNumbers,
      });

      menuItems.push({
         icon: 'block',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Blocked Phone Numbers',
         routerLink: blockedPhoneNumbersRoute,
         showInCollapsedMenu: true,
         tooltip: 'Blocked Phone Numbers',
         isHidden: isOnSystemPage || !canManageAccounts,
      });

      const canViewLocations = hasSelectedPrivilege(
         Privileges.ManageLocationSettings_View,
         account.accountId,
         privileges
      );
      menuItems.push({
         icon: 'location_on',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Locations',
         routerLink: locationsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Locations',
         isHidden: isOnSystemPage || !canViewLocations,
      });

      const canViewSchedules = hasSelectedPrivilege(Privileges.ManageSchedules_View, account.accountId, privileges);
      menuItems.push({
         icon: 'schedule',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Schedules',
         routerLink: schedulesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Schedules',
         isHidden: isOnSystemPage || !canViewSchedules,
      });

      const canViewDepartments = hasSelectedPrivilege(Privileges.ManageDepartments_View, account.accountId, privileges);
      menuItems.push({
         icon: 'business',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Departments',
         routerLink: departmentsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Departments',
         isHidden: isOnSystemPage || !canViewDepartments,
      });

      const canViewAccountPrompts = hasSelectedPrivilege(
         Privileges.ManageAccountPrompts_View,
         account.accountId,
         privileges
      );
      menuItems.push({
         icon: 'campaign',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Prompts',
         routerLink: promptsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Prompts',
         isHidden: isOnSystemPage || !canViewAccountPrompts,
      });

      menuItems.push({
         icon: 'devices',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Phones',
         routerLink: accountDevicesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Phones',
         isHidden: isOnSystemPage || !canViewDevices,
      });

      // Reports
      const canRunAtLeastOneReport = effectiveReports.length > 0;

      const reports: MenuItem[] = [];

      const hasExtensionDetail = hasSelectedReport(Reports.ExtensionDetail, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Extension Detail',
         routerLink: '/reports/extension-detail',
         isHidden: isOnSystemPage || !hasExtensionDetail,
      });

      const hasExtensionSummary = hasSelectedReport(
         Reports.ExtensionSummary,
         currentUser?.userId ?? 0,
         effectiveReports
      );

      reports.push({
         name: 'Extension Summary',
         routerLink: '/reports/extension-summary',
         isHidden: isOnSystemPage || !hasExtensionSummary,
      });

      const hasHourlyCallVolume = hasSelectedReport(
         Reports.HourlyCallVolume,
         currentUser?.userId ?? 0,
         effectiveReports
      );

      reports.push({
         name: 'Hourly Call Volume',
         routerLink: '/reports/hourly-call-volume',
         isHidden: isOnSystemPage || !hasHourlyCallVolume,
      });

      const hasTopCallers = hasSelectedReport(Reports.TopCallers, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Top Callers',
         routerLink: '/reports/top-callers',
         isHidden: isOnSystemPage || !hasTopCallers,
      });

      const hasDailyCallVolume = hasSelectedReport(Reports.DailyCallVolume, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Daily Call Volume',
         routerLink: '/reports/daily-call-volume',
         isHidden: isOnSystemPage || !hasDailyCallVolume,
      });

      const hasWeekDayCallVolume = hasSelectedReport(
         Reports.WeekDayCallVolume,
         currentUser?.userId ?? 0,
         effectiveReports
      );

      reports.push({
         name: 'Week Day Call Volume',
         routerLink: '/reports/weekday-call-volume',
         isHidden: isOnSystemPage || !hasWeekDayCallVolume,
      });

      const hasMissedCalls = hasSelectedReport(Reports.MissedCalls, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Missed Calls',
         routerLink: '/reports/missed-calls',
         isHidden: isOnSystemPage || !hasMissedCalls,
      });

      const hasHuntGroups = hasSelectedReport(Reports.HuntGroupExtension, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Hunt Groups',
         routerLink: '/reports/hunt-groups',
         isHidden: isOnSystemPage || !hasHuntGroups,
      });

      const hasInternationalCalls = hasSelectedReport(
         Reports.InternationalCalls,
         currentUser?.userId ?? 0,
         effectiveReports
      );

      reports.push({
         name: 'International Calls',
         routerLink: '/reports/international-calls',
         isHidden: isOnSystemPage || !hasInternationalCalls,
      });

      const hasAbandonedCalls = hasSelectedReport(Reports.AbandonedCalls, currentUser?.userId ?? 0, effectiveReports);

      reports.push({
         name: 'Abandoned Calls',
         routerLink: '/reports/abandoned-calls',
         isHidden: isOnSystemPage || !hasAbandonedCalls,
      });

      const hasInboundCalls = hasSelectedReport(
         Reports.InboundCallsByPhoneNumber,
         currentUser?.userId ?? 0,
         effectiveReports
      );

      reports.push({
         name: 'Inbound Calls by Phone Number',
         routerLink: '/reports/inbound-calls-by-phone-number',
         isHidden: isOnSystemPage || !hasInboundCalls,
      });
      const reportsMenu: MenuItem = {
         area: 'reports',
         children: reports,
         icon: 'insert_chart',
         isIndented: currentUser?.isTrueSystemUser,
         name: 'Reports',
         showInCollapsedMenu: true,
         tooltip: 'Reports',
         isHidden: isOnSystemPage || !canRunAtLeastOneReport,
      };
      menuItems.push(reportsMenu);

      menuItems.push({
         icon: 'toc',
         name: 'System Logs',
         isIndented: currentUser?.isTrueSystemUser,
         routerLink: accountLogsRoute,
         showInCollapsedMenu: true,
         tooltip: 'System Logs',
         isHidden: isOnSystemPage || !canViewSystemLogs,
      });

      menuItems.push({
         icon: 'phone_in_talk',
         name: 'Active Calls',
         routerLink: activeCallsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Active Calls',
         isHidden: !currentUser?.isTrueSystemUser || !canViewActiveCallPage,
      });

      menuItems.push({
         icon: 'block',
         name: 'Banned IPs',
         routerLink: bannedIpsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Banned IPs',
         isHidden: !currentUser?.isTrueSystemUser || !canManageBannedIps,
      });

      menuItems.push({
         icon: 'settings_ethernet',
         name: 'Allowed IPs',
         routerLink: allowedIpsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Allowed IPs',
         isHidden: !currentUser?.isTrueSystemUser,
      });
      menuItems.push({
         icon: 'dialpad',
         name: 'Phone Numbers',
         routerLink: phoneNumbersRoute,
         showInCollapsedMenu: true,
         tooltip: 'Phone Numbers',
         isHidden: !currentUser?.isTrueSystemUser || !canViewPhoneNumbers,
      });

      menuItems.push({
         icon: 'devices',
         name: 'Phones',
         routerLink: devicesRoute,
         showInCollapsedMenu: true,
         tooltip: 'Phones',
         isHidden: !currentUser?.isTrueSystemUser || !canViewDevices,
      });

      if (currentUser?.isTrueSystemUser) {
         const systemItems: MenuItem[] = [];
         const systemReportItems: MenuItem[] = [];

         const hasSystemInternationCalls = hasSelectedReport(
            Reports.SystemInternationalCalls,
            currentUser?.userId ?? 0,
            effectiveReports
         );

         const systemReportsMenu: MenuItem = {
            children: systemReportItems,
            icon: 'insert_chart',
            isIndented: false,
            name: 'System Reports',
            showInCollapsedMenu: true,
            tooltip: 'System Reports',
            isHidden: !hasSystemInternationCalls && !canViewSystemLogs,
         };

         systemReportItems.push({
            icon: 'insert_chart',
            name: 'System International Calls',
            routerLink: '/reports/system-international-calls',
            isHidden: !currentUser?.isTrueSystemUser || !hasSystemInternationCalls,
         });

         systemReportItems.push({
            icon: 'toc',
            name: 'System Logs',
            routerLink: systemLogsRoute,
            showInCollapsedMenu: true,
            tooltip: 'System Logs',
            isHidden: !currentUser?.isTrueSystemUser || !canViewSystemLogs,
         });
         systemItems.push(systemReportsMenu);

         systemItems.push({
            icon: 'person',
            name: 'System Users',
            routerLink: systemUsersRoute,
            showInCollapsedMenu: true,
            tooltip: 'Users',
            isHidden: !currentUser?.isTrueSystemUser || !canViewSystemUsers,
         });

         systemItems.push({
            icon: 'group',
            name: 'System Roles',
            routerLink: systemRolesRoute,
            showInCollapsedMenu: true,
            tooltip: 'Roles',
            isHidden: !currentUser?.isTrueSystemUser || !canViewSystemRoles,
         });

         const hasAnySystemItems = systemItems.some((item) => !item.isHidden);
         const systemMenu: MenuItem = {
            children: systemItems,
            icon: 'group',
            isIndented: false,
            name: 'System Management',
            showInCollapsedMenu: true,
            tooltip: 'System Management',
            isHidden: !currentUser?.isTrueSystemUser || !hasAnySystemItems,
         };
         menuItems.push(systemMenu);
      }
      menuItems.push({
         icon: 'attach_money',
         name: 'Call Costs',
         routerLink: callCostsRoute,
         showInCollapsedMenu: true,
         tooltip: 'Call Costs',
         isHidden: !currentUser?.isTrueSystemUser || !canViewCallCosts,
      });

      return menuItems;
   }
);
